import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Gift, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Public referral landing page.
 * When someone visits /r/:code, we:
 *  - store the code in localStorage under `hermes_ref` (so the Login page can auto-attach after signup)
 *  - fire an analytics ping so the referrer sees the click
 *  - show a friendly confirmation and redirect to the home / signup page
 */
export default function ReferralCapture() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading"); // loading | valid | invalid
  const [referrer, setReferrer] = useState(null);

  useEffect(() => {
    const cleanCode = (code || "").trim().toUpperCase();
    if (!cleanCode) { setStatus("invalid"); return; }
    // 1) save the code locally so we can attach it right after login
    try { localStorage.setItem("hermes_ref", cleanCode); } catch {}
    // 2) validate + track click in parallel
    Promise.all([
      api.get(`/referrals/lookup/${cleanCode}`).catch(() => ({ data: { valid: false }})),
      api.post("/referrals/track-click", { code: cleanCode }).catch(() => null),
    ]).then(([lookup]) => {
      if (lookup?.data?.valid) {
        setReferrer(lookup.data);
        setStatus("valid");
      } else {
        setStatus("invalid");
        try { localStorage.removeItem("hermes_ref"); } catch {}
      }
    });
  }, [code]);

  useEffect(() => {
    if (status === "loading") return;
    // auto-forward after 3s
    const t = setTimeout(() => navigate("/", { replace: true }), 3000);
    return () => clearTimeout(t);
  }, [status, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-black flex items-center justify-center p-6" data-testid="referral-capture">
      <div className="max-w-md w-full rounded-3xl bg-white/5 backdrop-blur-md border border-[#d4a24c]/30 p-8 shadow-2xl text-center">
        <div className="w-16 h-16 rounded-full bg-[#d4a24c]/20 mx-auto flex items-center justify-center border border-[#d4a24c]/50">
          {status === "valid" ? <CheckCircle2 className="w-8 h-8 text-[#d4a24c]"/> : <Gift className="w-8 h-8 text-[#d4a24c]"/>}
        </div>
        {status === "loading" && (
          <>
            <h1 className="font-heading text-2xl text-white mt-6" data-testid="referral-status-loading">Validando indicação...</h1>
            <p className="text-slate-300 text-sm mt-2">Aguarde um instante.</p>
          </>
        )}
        {status === "valid" && (
          <>
            <div className="text-[11px] uppercase tracking-widest text-[#d4a24c] mt-6">Convite recebido</div>
            <h1 className="font-heading text-2xl text-white mt-1" data-testid="referral-status-valid">
              Você foi indicado por {referrer?.referrer_name || "um advogado Hermes"}
            </h1>
            <p className="text-slate-300 text-sm mt-3">
              Continue para conhecer o Hermes — o app jurídico com IA feito para advogados.
            </p>
            <p className="text-slate-400 text-xs mt-6">Redirecionando em instantes...</p>
          </>
        )}
        {status === "invalid" && (
          <>
            <h1 className="font-heading text-2xl text-white mt-6" data-testid="referral-status-invalid">Indicação não encontrada</h1>
            <p className="text-slate-300 text-sm mt-2">
              Este código de indicação não é válido, mas você pode continuar e conhecer o Hermes.
            </p>
            <p className="text-slate-400 text-xs mt-6">Redirecionando em instantes...</p>
          </>
        )}
      </div>
    </div>
  );
}
